import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  User,
  Shield,
  Bell,
  Globe,
  Save,
  Code,
  Database,
  Trash2,
  Plug,
  Key,
  FileCheck,
  AlertTriangle,
  CreditCard,
  ArrowRight,
} from 'lucide-react';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { useSmsFeature, useFreeTrialFeature } from '@/contexts/FeatureFlagContext';
import { usePlanPricing } from '@/lib/planPricing';
import ApiKeys from '@/pages/Settings/ApiKeys';
import Automations from '@/pages/Settings/Automations';
import { ComplianceDashboard } from './Compliance/ComplianceDashboard';
import { HelpIcon } from './HelpIcon';
import { TooltipProvider } from './ui/tooltip';
import { getHelpContent } from '@/lib/helpContent';
import { VerticalPackManager } from './VerticalPackManager';

interface UserSettings {
  first_name: string;
  last_name: string;
  email: string;
  timezone: string;
  language: string;
  email_notifications: boolean;
  sms_notifications: boolean;
  marketing_emails: boolean;
}

interface TenantSettings {
  name: string;
  domain: string;
  timezone: string;
  default_language: string;
  email_from_name: string;
  email_from_address: string;
  support_email: string;
  privacy_policy_url?: string;
  terms_of_service_url?: string;
}

interface SecuritySettings {
  two_factor_enabled: boolean;
  session_timeout: number;
  password_requirements: {
    min_length: number;
    require_uppercase: boolean;
    require_lowercase: boolean;
    require_numbers: boolean;
    require_symbols: boolean;
  };
}

interface IntegrationSettings {
  postmark_api_token?: string;
  postmark_from_email?: string;
  twilio_account_sid?: string;
  twilio_auth_token?: string;
  twilio_phone_number?: string;
  google_places_api_key?: string;
  email_enabled?: boolean;
  sms_enabled?: boolean;
}

interface BillingInfo {
  plan: string;
  status: string;
  current_period_end?: string;
  stripe_customer_id?: string;
}

export default function SettingsPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isSmsEnabled = useSmsFeature();
  const isFreeTrialEnabled = useFreeTrialFeature();
  const { formattedPlanPrices } = usePlanPricing();
  
  // Get active tab from URL parameter, default to 'profile'
  const activeTab = searchParams.get('tab') || 'profile';
  const [userSettings, setUserSettings] = useState<UserSettings>({
    first_name: '',
    last_name: '',
    email: '',
    timezone: 'America/New_York',
    language: 'en',
    email_notifications: true,
    sms_notifications: false,
    marketing_emails: false,
  });
  const [tenantSettings, setTenantSettings] = useState<TenantSettings>({
    name: '',
    domain: '',
    timezone: 'America/New_York',
    default_language: 'en',
    email_from_name: '',
    email_from_address: '',
    support_email: '',
    privacy_policy_url: '',
    terms_of_service_url: '',
  });
  const [securitySettings] = useState<SecuritySettings>({
    two_factor_enabled: false,
    session_timeout: 30,
    password_requirements: {
      min_length: 8,
      require_uppercase: true,
      require_lowercase: true,
      require_numbers: true,
      require_symbols: false,
    },
  });
  const [integrationSettings, setIntegrationSettings] = useState<IntegrationSettings>({
    postmark_api_token: '',
    postmark_from_email: '',
    twilio_account_sid: '',
    twilio_auth_token: '',
    twilio_phone_number: '',
    google_places_api_key: '',
    email_enabled: false,
    sms_enabled: false,
  });
  const [billingInfo, setBillingInfo] = useState<BillingInfo | null>(null);
  const [features, setFeatures] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [generatingTestData, setGeneratingTestData] = useState(false);
  const [clearingTestData, setClearingTestData] = useState(false);
  const [testingIntegrations, setTestingIntegrations] = useState<{[key: string]: boolean}>({});

  // Fetch user and tenant settings on mount
  useEffect(() => {
    fetchSettings();
    fetchBillingInfo();
    fetchFeatures();
  }, []);

  // Handle hash fragment for scrolling to specific webhook
  useEffect(() => {
    const hash = window.location.hash;
    if (hash && hash.startsWith('#webhook-')) {
      // Wait for the Automations component to render, then scroll
      setTimeout(() => {
        const element = document.querySelector(hash);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 500);
    }
  }, [activeTab]);

  const fetchFeatures = async () => {
    try {
      const response = await api.billing.getFeatures();
      setFeatures(response.data.features);
    } catch (error) {
      console.error('Failed to load features:', error);
    }
  };

  const fetchSettings = async () => {
    try {
      setLoading(true);
      
      // Fetch user profile and tenant info in parallel
      const [userResponse, tenantResponse] = await Promise.all([
        api.user.getProfile(),
        api.tenant.getCurrent(),
      ]);

      // Update user settings
      const userData = userResponse.data;
      setUserSettings({
        first_name: userData.first_name || '',
        last_name: userData.last_name || '',
        email: userData.email || '',
        timezone: userData.timezone || 'America/New_York',
        language: userData.language || 'en',
        email_notifications: userData.email_notifications ?? true,
        sms_notifications: userData.sms_notifications ?? false,
        marketing_emails: userData.marketing_emails ?? false,
      });

      // Update tenant settings
      const tenantData = tenantResponse.data;
      setTenantSettings({
        name: tenantData.name || '',
        domain: tenantData.domain || '',
        timezone: tenantData.timezone || 'America/New_York',
        default_language: tenantData.default_language || 'en',
        email_from_name: tenantData.email_from_name || '',
        email_from_address: tenantData.email_from_address || '',
        support_email: tenantData.support_email || '',
        privacy_policy_url: tenantData.privacy_policy_url || '',
        terms_of_service_url: tenantData.terms_of_service_url || '',
      });

      // Fetch integration settings
      try {
        const integrationResponse = await api.integrations.get();
        if (integrationResponse.data) {
          setIntegrationSettings({
            postmark_api_token: integrationResponse.data.postmark_api_token || '',
            postmark_from_email: integrationResponse.data.postmark_from_email || '',
            twilio_account_sid: integrationResponse.data.twilio_account_sid || '',
            twilio_auth_token: integrationResponse.data.twilio_auth_token || '',
            twilio_phone_number: integrationResponse.data.twilio_phone_number || '',
            google_places_api_key: integrationResponse.data.google_places_api_key || '',
            email_enabled: integrationResponse.data.email_enabled || false,
            sms_enabled: integrationResponse.data.sms_enabled || false,
          });
        }
      } catch (err: any) {
        // Integration settings might not exist yet, which is okay
        console.debug('Integration settings not found, will use defaults');
      }
    } catch (err: any) {
      console.error('Error fetching settings:', err);
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const fetchBillingInfo = async () => {
    try {
      const response = await api.billing.getInfo();
      setBillingInfo(response.data);
    } catch (err: any) {
      console.error('Error fetching billing info:', err);
      // Don't show error toast for billing info as it might not be set up yet
    }
  };

  const handleUserSettingsChange = (field: keyof UserSettings, value: any) => {
    setUserSettings(prev => ({ ...prev, [field]: value }));
  };

  const handleTenantSettingsChange = (field: keyof TenantSettings, value: any) => {
    setTenantSettings(prev => ({ ...prev, [field]: value }));
  };

  const handleIntegrationSettingsChange = (field: keyof IntegrationSettings, value: any) => {
    setIntegrationSettings(prev => ({ ...prev, [field]: value }));
  };

  const handleSaveUserSettings = async () => {
    try {
      setSaving(true);
      await api.user.updateProfile(userSettings);
      toast.success('Profile settings saved successfully');
    } catch (err: any) {
      console.error('Error saving user settings:', err);
      toast.error(err.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveTenantSettings = async () => {
    try {
      setSaving(true);
      await api.tenant.update(tenantSettings);
      toast.success('Organization settings saved successfully');
    } catch (err: any) {
      console.error('Error saving tenant settings:', err);
      toast.error(err.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveIntegrationSettings = async () => {
    try {
      setSaving(true);
      // Only send non-empty values to avoid overwriting with empty strings
      const updateData: any = {};
      if (integrationSettings.postmark_api_token) updateData.postmark_api_token = integrationSettings.postmark_api_token;
      if (integrationSettings.postmark_from_email) updateData.postmark_from_email = integrationSettings.postmark_from_email;
      if (integrationSettings.twilio_account_sid) updateData.twilio_account_sid = integrationSettings.twilio_account_sid;
      if (integrationSettings.twilio_auth_token) updateData.twilio_auth_token = integrationSettings.twilio_auth_token;
      if (integrationSettings.twilio_phone_number) updateData.twilio_phone_number = integrationSettings.twilio_phone_number;
      if (integrationSettings.google_places_api_key) updateData.google_places_api_key = integrationSettings.google_places_api_key;
      if (integrationSettings.email_enabled !== undefined) updateData.email_enabled = integrationSettings.email_enabled;
      if (integrationSettings.sms_enabled !== undefined) updateData.sms_enabled = integrationSettings.sms_enabled;

      await api.integrations.update(updateData);
      toast.success('Integration settings saved successfully');
    } catch (err: any) {
      console.error('Error saving integration settings:', err);
      toast.error(err.response?.data?.detail || 'Failed to save integration settings');
    } finally {
      setSaving(false);
    }
  };

  const handleTestIntegration = async (integration: string) => {
    try {
      setTestingIntegrations(prev => ({ ...prev, [integration]: true }));
      
      if (integration === 'email') {
        // Test email integration - use current user's email or a test email
        const testEmail = userSettings.email || 'test@example.com';
        await api.integrations.testEmail({
          to_email: testEmail,
          subject: 'Test Email from ReputationManager',
          message: 'This is a test email to verify your email integration is working correctly.',
        });
        toast.success('Email integration test successful! Check your inbox.');
      } else if (integration === 'sms') {
        // Test SMS integration - require phone number input
        if (!integrationSettings.twilio_phone_number) {
          toast.error('Please enter a phone number to test SMS integration');
          return;
        }
        // Use the configured Twilio phone number or prompt for a test number
        const testPhone = integrationSettings.twilio_phone_number;
        await api.integrations.testSms({
          to_phone: testPhone,
          message: 'This is a test SMS to verify your SMS integration is working correctly.',
        });
        toast.success('SMS integration test successful! Check your phone.');
      } else {
        toast.error(`Unknown integration type: ${integration}`);
      }
    } catch (err: any) {
      console.error(`Error testing ${integration} integration:`, err);
      toast.error(`${integration} integration test failed: ${err.response?.data?.detail || 'Unknown error'}`);
    } finally {
      setTestingIntegrations(prev => ({ ...prev, [integration]: false }));
    }
  };

  const handleUpgradePlan = async (plan: string) => {
    try {
      const successUrl = `${window.location.origin}/settings?tab=integrations&success=true`;
      const cancelUrl = `${window.location.origin}/settings?tab=integrations&canceled=true`;
      
      const response = await api.billing.createCheckoutSession(plan, successUrl, cancelUrl);
      window.location.href = response.data.checkout_url;
    } catch (err: any) {
      console.error('Error creating checkout session:', err);
      toast.error('Failed to start checkout process');
    }
  };

  const handleManageBilling = async () => {
    try {
      const response = await api.billing.createPortalSession(window.location.origin + '/settings?tab=integrations');
      window.open(response.data.portal_url, '_blank');
    } catch (err: any) {
      console.error('Error opening billing portal:', err);
      toast.error('Failed to open billing portal');
    }
  };

  const handleGenerateTestData = async () => {
    try {
      setGeneratingTestData(true);
      const response = await api.testData.generate();
      const counts = response.data;
      toast.success(
        `Test data generated successfully! Created: ${counts.contacts} contacts, ${counts.templates} templates, ${counts.sequences} sequences, ${counts.campaigns} campaigns, ${counts.feedback} feedback entries`
      );
    } catch (err: any) {
      console.error('Error generating test data:', err);
      toast.error(err.response?.data?.detail || 'Failed to generate test data');
    } finally {
      setGeneratingTestData(false);
    }
  };

  const handleClearTestData = async () => {
    if (!confirm('Are you sure you want to clear all test data? This action cannot be undone.')) {
      return;
    }
    
    try {
      setClearingTestData(true);
      const response = await api.testData.clear();
      const counts = response.data;
      toast.success(
        `Test data cleared successfully! Deleted: ${counts.contacts} contacts, ${counts.templates} templates, ${counts.sequences} sequences, ${counts.campaigns} campaigns, ${counts.feedback} feedback entries`
      );
    } catch (err: any) {
      console.error('Error clearing test data:', err);
      toast.error(err.response?.data?.detail || 'Failed to clear test data');
    } finally {
      setClearingTestData(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="text-lg text-muted-foreground">Loading settings...</div>
        </div>
      </div>
    );
  }

  const complianceHelp = getHelpContent('complianceRequirements');
  const apiKeysHelp = getHelpContent('apiKeys');
  const securityHelp = getHelpContent('securityBestPractices');

  return (
    <TooltipProvider>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-3xl font-bold tracking-tight">Settings</h1>
            </div>
            <p className="text-muted-foreground">
              Manage your account and application preferences
            </p>
          </div>
        </div>

      <Tabs value={activeTab} onValueChange={(value) => navigate(`/settings?tab=${value}`, { replace: true })} className="space-y-6">
        <TabsList className={`grid w-full ${isSmsEnabled ? 'grid-cols-9' : 'grid-cols-8'}`}>
          <TabsTrigger value="profile" className="flex items-center gap-2">
            <User className="h-4 w-4" />
            Profile
          </TabsTrigger>
          <TabsTrigger value="tenant" className="flex items-center gap-2">
            <Globe className="h-4 w-4" />
            Organization
          </TabsTrigger>
          <TabsTrigger value="notifications" className="flex items-center gap-2">
            <Bell className="h-4 w-4" />
            Notifications
          </TabsTrigger>
          <TabsTrigger value="security" className="flex items-center gap-2">
            <Shield className="h-4 w-4" />
            Security
          </TabsTrigger>
          <TabsTrigger value="integrations" className="flex items-center gap-2">
            <Plug className="h-4 w-4" />
            Account
          </TabsTrigger>
          {features?.api_access && (
            <TabsTrigger value="api-keys" className="flex items-center gap-2">
              <Key className="h-4 w-4" />
              API Keys
              {apiKeysHelp && (
                <HelpIcon
                  tooltip="Learn about API keys"
                  modalTitle={apiKeysHelp.title}
                  modalContent={apiKeysHelp.content}
                  size="sm"
                  variant="subtle"
                  className="ml-1"
                />
              )}
            </TabsTrigger>
          )}
          {features?.api_access && (
            <TabsTrigger value="automations" className="flex items-center gap-2">
              <Plug className="h-4 w-4" />
              Automations
            </TabsTrigger>
          )}
          {isSmsEnabled && (
            <TabsTrigger value="compliance" className="flex items-center gap-2">
              <FileCheck className="h-4 w-4" />
              Compliance
              {complianceHelp && (
                <HelpIcon
                  tooltip="Learn about compliance"
                  modalTitle={complianceHelp.title}
                  modalContent={complianceHelp.content}
                  size="sm"
                  variant="subtle"
                  className="ml-1"
                />
              )}
            </TabsTrigger>
          )}
          <TabsTrigger value="developer" className="flex items-center gap-2">
            <Code className="h-4 w-4" />
            Developer
          </TabsTrigger>
        </TabsList>

        {/* Profile Settings */}
        <TabsContent value="profile" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="h-5 w-5" />
                Personal Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="first_name">First Name</Label>
                  <Input
                    id="first_name"
                    value={userSettings.first_name}
                    onChange={(e) => handleUserSettingsChange('first_name', e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="last_name">Last Name</Label>
                  <Input
                    id="last_name"
                    value={userSettings.last_name}
                    onChange={(e) => handleUserSettingsChange('last_name', e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={userSettings.email}
                    onChange={(e) => handleUserSettingsChange('email', e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="timezone">Timezone</Label>
                  <Select
                    value={userSettings.timezone}
                    onValueChange={(value) => handleUserSettingsChange('timezone', value)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="UTC">UTC</SelectItem>
                      <SelectItem value="America/New_York">Eastern Time</SelectItem>
                      <SelectItem value="America/Chicago">Central Time</SelectItem>
                      <SelectItem value="America/Denver">Mountain Time</SelectItem>
                      <SelectItem value="America/Los_Angeles">Pacific Time</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="language">Language</Label>
                  <Select
                    value={userSettings.language}
                    onValueChange={(value) => handleUserSettingsChange('language', value)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="en">English</SelectItem>
                      <SelectItem value="es">Spanish</SelectItem>
                      <SelectItem value="fr">French</SelectItem>
                      <SelectItem value="de">German</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="flex justify-end">
                <Button 
                  onClick={handleSaveUserSettings}
                  disabled={saving}
                >
                  <Save className="h-4 w-4 mr-2" />
                  {saving ? 'Saving...' : 'Save Changes'}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Billing Section */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CreditCard className="h-5 w-5" />
                Billing & Subscription
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between p-4 border rounded-lg bg-muted/50">
                <div>
                  <p className="font-medium">Manage your billing and subscription</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    View usage, update your plan, and manage payment methods
                  </p>
                </div>
                <Button
                  onClick={() => navigate('/billing')}
                  variant="default"
                  className="flex items-center gap-2"
                >
                  Go to Billing
                  <ArrowRight className="h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Organization Settings */}
        <TabsContent value="tenant" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Globe className="h-5 w-5" />
                Organization Settings
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="tenant_name">Organization Name</Label>
                  <Input
                    id="tenant_name"
                    value={tenantSettings.name}
                    onChange={(e) => handleTenantSettingsChange('name', e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="domain">Domain</Label>
                  <Input
                    id="domain"
                    value={tenantSettings.domain}
                    onChange={(e) => handleTenantSettingsChange('domain', e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email_from_name">Email From Name</Label>
                  <Input
                    id="email_from_name"
                    value={tenantSettings.email_from_name}
                    onChange={(e) => handleTenantSettingsChange('email_from_name', e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email_from_address">Email From Address</Label>
                  <Input
                    id="email_from_address"
                    type="email"
                    value={tenantSettings.email_from_address}
                    onChange={(e) => handleTenantSettingsChange('email_from_address', e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="support_email">Support Email</Label>
                  <Input
                    id="support_email"
                    type="email"
                    value={tenantSettings.support_email}
                    onChange={(e) => handleTenantSettingsChange('support_email', e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="tenant_timezone">Timezone</Label>
                  <Select
                    value={tenantSettings.timezone}
                    onValueChange={(value) => handleTenantSettingsChange('timezone', value)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="UTC">UTC</SelectItem>
                      <SelectItem value="America/New_York">Eastern Time</SelectItem>
                      <SelectItem value="America/Chicago">Central Time</SelectItem>
                      <SelectItem value="America/Denver">Mountain Time</SelectItem>
                      <SelectItem value="America/Los_Angeles">Pacific Time</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="flex justify-end">
                <Button 
                  onClick={handleSaveTenantSettings}
                  disabled={saving}
                >
                  <Save className="h-4 w-4 mr-2" />
                  {saving ? 'Saving...' : 'Save Changes'}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Vertical Pack Section */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Database className="h-5 w-5" />
                Vertical Pack
              </CardTitle>
            </CardHeader>
            <CardContent>
              <VerticalPackManager compact={true} />
            </CardContent>
          </Card>
        </TabsContent>

        {/* Notification Settings */}
        <TabsContent value="notifications" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Bell className="h-5 w-5" />
                Notification Preferences
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <Label htmlFor="email_notifications">Email Notifications</Label>
                    <p className="text-sm text-muted-foreground">
                      Receive important updates via email
                    </p>
                  </div>
                  <input
                    type="checkbox"
                    id="email_notifications"
                    checked={userSettings.email_notifications}
                    onChange={(e) => handleUserSettingsChange('email_notifications', e.target.checked)}
                    className="h-4 w-4"
                  />
                </div>
                {isSmsEnabled && (
                  <div className="flex items-center justify-between">
                    <div>
                      <Label htmlFor="sms_notifications">SMS Notifications</Label>
                      <p className="text-sm text-muted-foreground">
                        Receive urgent alerts via SMS
                      </p>
                    </div>
                    <input
                      type="checkbox"
                      id="sms_notifications"
                      checked={userSettings.sms_notifications}
                      onChange={(e) => handleUserSettingsChange('sms_notifications', e.target.checked)}
                      className="h-4 w-4"
                    />
                  </div>
                )}
                <div className="flex items-center justify-between">
                  <div>
                    <Label htmlFor="marketing_emails">Marketing Emails</Label>
                    <p className="text-sm text-muted-foreground">
                      Receive product updates and promotional content
                    </p>
                  </div>
                  <input
                    type="checkbox"
                    id="marketing_emails"
                    checked={userSettings.marketing_emails}
                    onChange={(e) => handleUserSettingsChange('marketing_emails', e.target.checked)}
                    className="h-4 w-4"
                  />
                </div>
              </div>
              <div className="flex justify-end">
                <Button 
                  onClick={handleSaveUserSettings}
                  disabled={saving}
                >
                  <Save className="h-4 w-4 mr-2" />
                  {saving ? 'Saving...' : 'Save Changes'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Security Settings */}
        <TabsContent value="security" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5" />
                Security Settings
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <Label htmlFor="two_factor">Two-Factor Authentication</Label>
                    <p className="text-sm text-muted-foreground">
                      Add an extra layer of security to your account
                    </p>
                  </div>
                  <input
                    type="checkbox"
                    id="two_factor"
                    checked={securitySettings.two_factor_enabled}
                    disabled
                    className="h-4 w-4"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="session_timeout">Session Timeout (minutes)</Label>
                  <Input
                    id="session_timeout"
                    type="number"
                    value={securitySettings.session_timeout}
                    disabled
                    min="5"
                    max="480"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Password Requirements</Label>
                <div className="space-y-2 pl-4">
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={securitySettings.password_requirements.require_uppercase}
                      disabled
                      className="h-4 w-4"
                    />
                    <Label className="text-sm">Require uppercase letters</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={securitySettings.password_requirements.require_lowercase}
                      disabled
                      className="h-4 w-4"
                    />
                    <Label className="text-sm">Require lowercase letters</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={securitySettings.password_requirements.require_numbers}
                      disabled
                      className="h-4 w-4"
                    />
                    <Label className="text-sm">Require numbers</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={securitySettings.password_requirements.require_symbols}
                      disabled
                      className="h-4 w-4"
                    />
                    <Label className="text-sm">Require symbols</Label>
                  </div>
                </div>
              </div>
              <p className="text-sm text-muted-foreground italic">
                Security settings are managed by system administrators.
              </p>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Integration Settings */}
        <TabsContent value="integrations" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Plug className="h-5 w-5" />
                Account Settings
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Payment & Subscription Management */}
              <div className="border-l-4 border-purple-500 bg-purple-50 p-4 rounded">
                <h4 className="font-semibold text-purple-900 mb-2">Payment & Subscription</h4>
                <div className="space-y-4">
                  {billingInfo ? (
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-gray-700">Current Plan</p>
                          <p className="text-lg font-semibold text-purple-900 capitalize">{billingInfo.plan}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-medium text-gray-700">Status</p>
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            billingInfo.status === 'active' 
                              ? 'bg-green-100 text-green-800' 
                              : billingInfo.status === 'trialing'
                              ? 'bg-blue-100 text-blue-800'
                              : billingInfo.status === 'past_due'
                              ? 'bg-yellow-100 text-yellow-800'
                              : 'bg-red-100 text-red-800'
                          }`}>
                            {billingInfo.status}
                          </span>
                        </div>
                      </div>
                      
                      {billingInfo.current_period_end && (
                        <div>
                          <p className="text-sm font-medium text-gray-700">Next Billing Date</p>
                          <p className="text-sm text-gray-600">
                            {new Date(billingInfo.current_period_end).toLocaleDateString()}
                          </p>
                        </div>
                      )}

                      {(!isFreeTrialEnabled || billingInfo.status === 'incomplete') && (
                        <div className="bg-red-50 border border-red-200 rounded-md p-3">
                          <div className="flex">
                            <div className="flex-shrink-0">
                              <AlertTriangle className="h-5 w-5 text-red-400" />
                            </div>
                            <div className="ml-3">
                              <h3 className="text-sm font-medium text-red-800">Payment Required</h3>
                              <div className="mt-1 text-sm text-red-700">
                                <p>Add payment method to continue using all features.</p>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      <div className="flex gap-2 pt-2">
                        <Button
                          onClick={() => handleUpgradePlan('growth')}
                          variant="outline"
                          size="sm"
                          disabled={billingInfo.plan === 'growth' || billingInfo.plan === 'agency'}
                        >
                          Upgrade to Growth
                        </Button>
                        <Button
                          onClick={() => handleUpgradePlan('agency')}
                          variant="outline"
                          size="sm"
                          disabled={billingInfo.plan === 'agency'}
                        >
                          Upgrade to Agency
                        </Button>
                        <Button
                          onClick={handleManageBilling}
                          variant="default"
                          size="sm"
                          className="bg-purple-600 hover:bg-purple-700"
                        >
                          Manage Billing
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <p className="text-sm text-gray-600">
                        No active subscription found. Choose a plan to get started:
                      </p>
                      <div className="flex gap-2">
                        <Button
                          onClick={() => handleUpgradePlan('starter')}
                          variant="outline"
                          size="sm"
                        >
                          Starter Plan - {formattedPlanPrices.starter}/month
                        </Button>
                        <Button
                          onClick={() => handleUpgradePlan('growth')}
                          variant="outline"
                          size="sm"
                        >
                          Growth Plan - {formattedPlanPrices.growth}/month
                        </Button>
                        <Button
                          onClick={() => handleUpgradePlan('agency')}
                          variant="outline"
                          size="sm"
                        >
                          Agency Plan - {formattedPlanPrices.agency}/month
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* API Keys Settings */}
        <TabsContent value="api-keys" className="space-y-6">
          <ApiKeys />
        </TabsContent>

        {/* Automations & Webhooks Settings */}
        {features?.api_access && (
          <TabsContent value="automations" className="space-y-6">
            <Automations />
          </TabsContent>
        )}

        {/* Compliance Dashboard - Only show when SMS is enabled */}
        {isSmsEnabled && (
          <TabsContent value="compliance" className="space-y-6">
            <ComplianceDashboard />
          </TabsContent>
        )}

        {/* Developer Settings */}
        <TabsContent value="developer" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Code className="h-5 w-5" />
                Test Data Management
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-4">
                <div className="border-l-4 border-blue-500 bg-blue-50 p-4 rounded">
                  <div className="flex items-start gap-3">
                    <Database className="h-5 w-5 text-blue-600 mt-0.5" />
                    <div className="flex-1">
                      <h4 className="font-semibold text-blue-900">Generate Test Data</h4>
                      <p className="text-sm text-blue-700 mt-1">
                        Create sample contacts, templates, campaigns, and feedback entries for testing and demonstration purposes.
                      </p>
                      <div className="mt-3">
                        <Button
                          onClick={handleGenerateTestData}
                          disabled={generatingTestData}
                          variant="default"
                          className="bg-blue-600 hover:bg-blue-700"
                        >
                          <Database className="h-4 w-4 mr-2" />
                          {generatingTestData ? 'Generating...' : 'Generate Test Data'}
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="border-l-4 border-red-500 bg-red-50 p-4 rounded">
                  <div className="flex items-start gap-3">
                    <Trash2 className="h-5 w-5 text-red-600 mt-0.5" />
                    <div className="flex-1">
                      <h4 className="font-semibold text-red-900">Clear Test Data</h4>
                      <p className="text-sm text-red-700 mt-1">
                        Remove all test data from your organization. This will delete test contacts, templates, campaigns, and feedback entries.
                      </p>
                      <div className="mt-3">
                        <Button
                          onClick={handleClearTestData}
                          disabled={clearingTestData}
                          variant="destructive"
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          {clearingTestData ? 'Clearing...' : 'Clear Test Data'}
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <p className="text-sm text-yellow-800">
                  <strong>Note:</strong> Only organization owners can generate or clear test data. Test data is identified by specific prefixes (e.g., "Test" for templates/campaigns, "test." for contact emails).
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
      </div>
    </TooltipProvider>
  );
}
